/**
 * Presentation-only yaw easing (`q-walk-turn-restarts-stride`, owner Desk #849).
 *
 * The avatar group's `rotation.y` was set straight to the simulated facing each frame,
 * so a change of direction snapped the body to its new heading in one frame — with no
 * turning motion, a walk that keeps changing direction read as a stall. This eases the
 * RENDERED yaw toward the facing along the shortest arc.
 *
 * Only the mesh is eased. The simulated facing (`predicted.facing`, the wire `facing`)
 * is never touched: the server, the dodge direction and every hit test keep using it
 * (AD-031: presentation stays off the per-tick path).
 *
 * Pure + node-safe: no `three`.
 */

/**
 * Time constant of the turn, seconds: ~63 % of the way in this long, ~95 % in three.
 * A 90° change of direction reads as a turn (~0.2 s) without the body lagging the path.
 */
export const YAW_EASE_SECONDS = 0.07;

/** `a` wrapped into (-π, π]. Non-finite ⇒ 0. */
function wrap(a: number): number {
  if (!Number.isFinite(a)) return 0;
  let r = a % (2 * Math.PI);
  if (r > Math.PI) r -= 2 * Math.PI;
  if (r <= -Math.PI) r += 2 * Math.PI;
  return r;
}

/**
 * The rendered yaw after `dtMs` of easing from `current` toward `target`, along the
 * shortest arc (so a turn across ±π goes the short way, never a full spin). A
 * non-finite `current` snaps to the target; a non-finite `target` or `dtMs` ≤ 0 keeps
 * `current`. The result is wrapped into (-π, π].
 */
export function easeYaw(
  current: number,
  target: number,
  dtMs: number,
  tauSeconds = YAW_EASE_SECONDS,
): number {
  if (!Number.isFinite(target)) return current;
  if (!Number.isFinite(current)) return wrap(target);
  if (!(dtMs > 0)) return current;
  const delta = wrap(target - current);
  if (!(tauSeconds > 0)) return wrap(target);
  const k = 1 - Math.exp(-dtMs / 1000 / tauSeconds);
  return wrap(current + delta * k);
}
